import { BotMessageSquare, File } from "lucide-react";
import { useDeferredValue, useMemo, useState, type FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { type ConversationMessageApiModel as ChatMessageType, ROLE_VALUES } from "@noa/api-core";
import { parseMessageWithReferences, useMessageReferenceDocs } from "@noa/api-client";
import { parseMarkdownIntoSegments, hasAtLeastOneCarousel, hasAtLeastOneTimeline, hasAtLeastOneAction } from "@noa/ui";
import { useAppConfigs } from "~/hooks/use-app-configs";
import { useSelectedMessageId, useSetSelectedMessageId } from "@noa/client-store";
import { ChatMessageTools } from "./chat-message-tools";
import { ChatMessageReferences } from "./reference/chat-message-references";
import { useActionDescription, useChatbotState, useMessageActionDescription } from "@noa/client-store";
import { useDeduplicatedImages } from "~/hooks/chat/use-deduplicated-images";
import { ChatMessageAnchor } from "./chat-message-anchor";
import { getMarkdownReadyToDisplay } from "~/utils/markdown.utils";
import { removeParagraphImageWrapper } from "~/utils/html.utils";
import { rehypeTableCaption } from "~/utils/rehype-table-caption";
import { rehypeTableDataLabels } from "~/utils/rehype-table-data-labels";
import { AuthenticatedImage } from "./chat-message-authenticated-image";
import { ChatMessageImageGallery } from "./chat-message-img-gallery";
import { ChatMessageCarousel } from "./chat-message-carousel";
import { ChatMessageTimeline } from "./chat-message-timeline";
import { ActionCard } from "./action-card";
import { ReferenceDocumentsList } from "~/components/reference-docs/reference-documents-list";
import { Avatar, Card, Tooltip } from "~/components/ui";

import classnames from "classnames";

interface ChatMessageProps {
  chatMessage: ChatMessageType;
  index: number;
  isLastMessage: boolean;
}

export const ChatMessage: FunctionComponent<ChatMessageProps> = ({ chatMessage, index, isLastMessage }) => {
  const { t } = useTranslation();

  const chatbotState = useChatbotState();
  const setSelectedMessageId = useSetSelectedMessageId();
  const selectedMessageId = useSelectedMessageId();
  const { appConfigs } = useAppConfigs();

  const isAssistant = chatMessage.role === ROLE_VALUES.ASSISTANT;
  const { content, references } = parseMessageWithReferences(chatMessage.content);
  const storedActionDescription = useMessageActionDescription(chatMessage.message_id);
  const transientActionDescription = useActionDescription();

  const onMessageSelection = () => {
    setSelectedMessageId(chatMessage.message_id);
  };

  const isSelected = chatMessage.message_id === selectedMessageId;
  const isLastMessageCompleted = isLastMessage && chatbotState === "waiting";
  const isStreaming = chatbotState !== null && chatbotState !== "waiting";
  // A message is complete when it has a message_id (assigned at end of bubble)
  const isMessageComplete = chatMessage.message_id !== "";
  // Show transient action description for streaming message, stored for completed messages
  const actionDescriptionToShow = isMessageComplete
    ? storedActionDescription
    : isLastMessage
      ? transientActionDescription
      : null;
  const shouldDisplayChatTools = index > 0 && isAssistant && (!isLastMessage || isLastMessageCompleted);

  const { uniqueSources } = useDeduplicatedImages(chatMessage.used_sources);

  const [showDocumentRefs, setShowDocumentRefs] = useState(false);
  const {
    documents: mobileDocuments,
    referenceMap: mobileReferenceMap,
    hasDocuments: hasDocumentsForMobile,
  } = useMessageReferenceDocs(chatMessage, isAssistant);

  const markdownReadyToDisplay = getMarkdownReadyToDisplay(content, isStreaming);
  // Use deferred value to prevent UI blocking during rapid streaming updates
  const deferredMarkdown = useDeferredValue(markdownReadyToDisplay);
  const sourceOnlyImages = uniqueSources.filter((source) => !deferredMarkdown?.includes(source.image_url!));

  const contentSegments = useMemo(
    () => (isMessageComplete && deferredMarkdown ? parseMarkdownIntoSegments(deferredMarkdown) : []),
    [isMessageComplete, deferredMarkdown]
  );
  const isSegmentedContent =
    hasAtLeastOneCarousel(contentSegments) ||
    hasAtLeastOneTimeline(contentSegments) ||
    hasAtLeastOneAction(contentSegments);

  return (
    <div>
      {isAssistant && actionDescriptionToShow && (
        <div className="chat-action-description">{actionDescriptionToShow}</div>
      )}
      <Card className={classnames("chat-message", { assistant: isAssistant, selected: isSelected })} size="small">
        <div className={classnames("flex items-start gap-3", isAssistant ? "justify-start" : "justify-end")}>
          {isAssistant && (
            <>
              <Avatar
                className={classnames("chat-avatar", hasDocumentsForMobile ? "hidden sm:flex" : "")}
                icon={<BotMessageSquare className="chat-avatar-icon" />}
                onClick={onMessageSelection}
              />
              {hasDocumentsForMobile && (
                <label className="chat-avatar-toggle sm:hidden">
                  <input
                    type="checkbox"
                    checked={showDocumentRefs}
                    onChange={(e) => setShowDocumentRefs(e.target.checked)}
                  />
                  <Tooltip title={t("chat:message.showMessage")} placement="right">
                    <BotMessageSquare
                      className={showDocumentRefs ? "chat-icon-inactive" : "chat-icon-active"}
                      size={18}
                    />
                  </Tooltip>
                  <Tooltip title={t("chat:message.showReferencedDocuments")} placement="right">
                    <File className={showDocumentRefs ? "chat-icon-active" : "chat-icon-inactive"} size={18} />
                  </Tooltip>
                </label>
              )}
            </>
          )}

          <div className="flex-1 min-w-0 pt-1 self-center chat-message-content">
            {hasDocumentsForMobile && (
              <div className={classnames(showDocumentRefs ? "sm:hidden" : "hidden")}>
                <ReferenceDocumentsList documents={mobileDocuments} referenceMap={mobileReferenceMap} />
              </div>
            )}
            <div className={classnames(showDocumentRefs && hasDocumentsForMobile ? "hidden sm:block" : "")}>
              {isSegmentedContent ? (
                <>
                  {contentSegments.map((seg, idx) =>
                    seg.type === "carousel" ? (
                      <ChatMessageCarousel key={idx} slides={seg.slides} />
                    ) : seg.type === "timeline" ? (
                      <ChatMessageTimeline key={idx} items={seg.items} title={seg.title} />
                    ) : seg.type === "action" ? (
                      <ActionCard key={idx} actionType={seg.actionType} label={seg.label} data={seg.data} />
                    ) : (
                      <Markdown
                        key={idx}
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw, rehypeTableCaption, rehypeTableDataLabels]}
                        components={{
                          img: ({ src, alt }) => <AuthenticatedImage url={src ?? ""} alt={alt ?? ""} />,
                          p: removeParagraphImageWrapper,
                          a: ({ href, children }) => <ChatMessageAnchor href={href}>{children}</ChatMessageAnchor>,
                        }}
                      >
                        {seg.content}
                      </Markdown>
                    )
                  )}
                  <ChatMessageImageGallery authenticatedImages={sourceOnlyImages} isStreaming={false} />
                </>
              ) : (
                <>
                  <Markdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw, rehypeTableCaption, rehypeTableDataLabels]}
                    components={{
                      img: ({ src, alt }) => {
                        if (!isMessageComplete) return null;
                        return <AuthenticatedImage url={src ?? ""} alt={alt ?? ""} />;
                      },
                      p: removeParagraphImageWrapper,
                      a: ({ href, children }) => <ChatMessageAnchor href={href}>{children}</ChatMessageAnchor>,
                    }}
                  >
                    {deferredMarkdown || t("chat:message.analyzing")}
                  </Markdown>
                  <ChatMessageImageGallery authenticatedImages={sourceOnlyImages} isStreaming={!isMessageComplete} />
                </>
              )}

              {appConfigs?.chatOptions.showReferenceDocumentsSummary && (
                <ChatMessageReferences references={references} chatMessage={chatMessage} />
              )}
            </div>
          </div>
        </div>
      </Card>

      {shouldDisplayChatTools && <ChatMessageTools chatMessage={chatMessage} />}
    </div>
  );
};
